import React, { useCallback, useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { useTranslation } from "react-i18next";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import {
  gettype,
  getsubtype,
  getexercise,
  addexercise,
  updateexercise,
} from "../../components/funtionsBD/Mainservice";
import { isPersonalizedExerciseType, isSubtypeFreeExerciseType, isKingOfTableExerciseType } from "../../utils/teExecutionType";
import { catalogFilterFromTrainingBlock } from "../../utils/addExerciseNavigation";
import { isTypeOnlyBlock } from "../../utils/planningSubgroups";
import { pickKotMatchSimulationExercise } from "../../utils/kotMatchSimulation";
import {
  LIVE_TRAINING_DIALOG_Z_INDEX,
  useLiveTrainingPortalRoot,
} from "../../hooks/useLiveFullscreen";
import { fetchGroupSmartRoster } from "../../components/funtionsBD/digipingSmartService";
import {
  getExercisePref,
  sortExercisesByCoachPref,
  toggleExerciseFavorite,
  setExerciseRating,
} from "../../utils/coachExerciseFavorites";
import Loader from "../../components/Loading/Loader";

function normalizeDuration(value) {
  const parsed = parseInt(String(value ?? "").trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 10;
}

function localizedName(row, prefix, lng) {
  if (!row) return "";
  const base = (lng || "en").split("-")[0];
  return (
    row[`${prefix}_${base}`] ||
    row[`${prefix}_en`] ||
    row[`${prefix}_fr`] ||
    row.name ||
    ""
  );
}

/**
 * In-session wizard: type → subtype (when needed) → catalog exercise → attach.
 * mode="replace" swaps the catalog exercise on an existing training block.
 */
export function LiveTrainingAddExerciseDialog({
  open,
  onClose,
  tsId,
  groupId,
  tsgId = null,
  onAdded,
  mode = "add",
  replaceTeId = null,
  existingExId = null,
  presetBlock = null,
  t: tProp,
}) {
  const { t: tHook, i18n } = useTranslation();
  const t = tProp || tHook;
  const portalRoot = useLiveTrainingPortalRoot();
  const lng = localStorage.getItem("i18nextLng") || i18n.language || "en";
  const isReplace = mode === "replace" && Boolean(replaceTeId);

  const [step, setStep] = useState("type"); // type | subtype | exercise | personalized
  const [slotPlayers, setSlotPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSubtype, setSelectedSubtype] = useState(null);
  const [selectedExId, setSelectedExId] = useState(null);
  const [durationMin, setDurationMin] = useState(10);
  const [prefTick, setPrefTick] = useState(0);

  const reset = useCallback(() => {
    setStep("type");
    setLoading(false);
    setSubmitting(false);
    setError("");
    setTypes([]);
    setSubtypes([]);
    setExercises([]);
    setSelectedType(null);
    setSelectedSubtype(null);
    setSelectedExId(null);
    setDurationMin(10);
    setSlotPlayers([]);
  }, []);

  const loadExercises = useCallback(
    async (typeRow, subtypeRow, cbId = null) => {
      setLoading(true);
      setError("");
      setExercises([]);
      setSelectedExId(null);
      try {
        const params = {
          type_id: typeRow?.type_id,
          sub_type_id: subtypeRow?.sub_type_id || null,
          cb_id: cbId || null,
          group_id: groupId || null,
        };
        const response = await getexercise(params);
        const list = sortExercisesByCoachPref(
          (response?.data?.data || []).map((row) => ({
            ...row,
            duration: normalizeDuration(row.duration ?? 10),
          }))
        );
        setExercises(list);
        if (list.length === 0) {
          setError(t("te_not_found") || "No exercises found.");
        } else {
          const kotDefault = isKingOfTableExerciseType(typeRow)
            ? pickKotMatchSimulationExercise(list)
            : null;
          if (kotDefault?.ex_id) {
            setSelectedExId(kotDefault.ex_id);
            setDurationMin(normalizeDuration(kotDefault.duration));
          } else {
            setDurationMin(list[0].duration);
          }
        }
        setStep("exercise");
      } catch (err) {
        setError(err?.message || "Failed to load exercises");
      } finally {
        setLoading(false);
      }
    },
    [groupId, t]
  );

  const loadSlotPlayers = useCallback(async () => {
    try {
      const roster = await fetchGroupSmartRoster({
        grId: groupId || null,
        lang: (lng || "en").split("-")[0],
      });
      const list = roster?.players || [];
      const filtered = groupId
        ? list.filter((p) => (p.gr_ids || []).map(String).includes(String(groupId)))
        : list;
      setSlotPlayers(filtered.length ? filtered : list);
    } catch {
      setSlotPlayers([]);
    }
  }, [groupId, lng]);

  useEffect(() => {
    if (!open) {
      reset();
      return undefined;
    }
    let cancelled = false;

    const matchTypeRow = (typeList, filter) =>
      typeList.find((row) => String(row.type_id) === String(filter.typeId)) ||
      typeList.find(
        (row) =>
          filter.typeNameEn &&
          String(row.type_name_en || "").trim() === String(filter.typeNameEn).trim()
      ) ||
      null;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const response = await gettype();
        if (cancelled) return;
        const typeList = response?.data?.data || [];
        setTypes(typeList);

        const shouldPreset = isTypeOnlyBlock(presetBlock);
        const filter = shouldPreset
          ? catalogFilterFromTrainingBlock(presetBlock, lng)
          : null;
        const typeRow = filter ? matchTypeRow(typeList, filter) : null;

        if (!typeRow) {
          setStep("type");
          setLoading(false);
          return;
        }

        setSelectedType(typeRow);
        if (isPersonalizedExerciseType(typeRow)) {
          setStep("personalized");
          await loadSlotPlayers();
          if (!cancelled) setLoading(false);
          return;
        }

        const subtypeRow = filter.subTypeId
          ? {
              sub_type_id: filter.subTypeId,
              sub_type_name_en: filter.label,
            }
          : null;
        if (subtypeRow) setSelectedSubtype(subtypeRow);
        await loadExercises(typeRow, subtypeRow, filter.cbId);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || t("general") || "Failed to load types");
          setStep("type");
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    reset,
    t,
    lng,
    presetBlock,
    loadExercises,
    loadSlotPlayers,
  ]);

  const handleSelectType = async (typeRow) => {
    setSelectedType(typeRow);
    setSelectedSubtype(null);
    setError("");
    if (isPersonalizedExerciseType(typeRow)) {
      setStep("personalized");
      setLoading(true);
      try {
        await loadSlotPlayers();
      } finally {
        setLoading(false);
      }
      return;
    }
    if (isSubtypeFreeExerciseType(typeRow)) {
      await loadExercises(typeRow, null);
      return;
    }
    setLoading(true);
    try {
      const response = await getsubtype(typeRow.type_id);
      const list = response?.data?.data || [];
      if (list.length === 0) {
        await loadExercises(typeRow, null);
        return;
      }
      setSubtypes(list);
      setStep("subtype");
    } catch (err) {
      setError(err?.message || "Failed to load subtypes");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSubtype = async (subtypeRow) => {
    setSelectedSubtype(subtypeRow);
    await loadExercises(selectedType, subtypeRow);
  };

  const handleBack = () => {
    setError("");
    if (step === "exercise") {
      if (selectedSubtype && subtypes.length > 0) {
        setStep("subtype");
        setExercises([]);
        setSelectedExId(null);
      } else {
        setStep("type");
        setSelectedType(null);
        setSelectedSubtype(null);
        setExercises([]);
      }
      return;
    }
    if (step === "personalized") {
      setStep("type");
      setSelectedType(null);
      setSlotPlayers([]);
      return;
    }
    if (step === "subtype") {
      setStep("type");
      setSelectedType(null);
      setSubtypes([]);
      setSelectedSubtype(null);
    }
  };

  const handleAddPersonalized = async () => {
    if (submitting) return;
    if (!isReplace && !tsId) return;
    if (isReplace && !replaceTeId) return;
    setSubmitting(true);
    setError("");
    try {
      await addexercise({
        ts_id: tsId,
        allow_duration_exceed: true,
        tsg_id: !isReplace && tsgId ? tsgId : undefined,
        te_id: isReplace ? replaceTeId : undefined,
        ex_ids: [
          {
            personalized_slot: true,
            duration: normalizeDuration(durationMin),
            steps: "1",
          },
        ],
      });
      onAdded?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || t("general") || "Failed to add exercise");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedExId || submitting) return;
    if (!isReplace && !tsId) return;
    if (isReplace && !replaceTeId) return;
    setSubmitting(true);
    setError("");
    try {
      if (isReplace) {
        await updateexercise({
          te_id: replaceTeId,
          replace_ex_id: selectedExId,
          existing_ex_id: existingExId || undefined,
        });
      } else {
        await addexercise({
          ts_id: tsId,
          allow_duration_exceed: true,
          tsg_id: tsgId || undefined,
          ex_ids: [
            {
              ex_id: selectedExId,
              duration: normalizeDuration(durationMin),
              steps: "1",
            },
          ],
        });
      }
      onAdded?.();
      onClose?.();
    } catch (err) {
      setError(
        err?.message ||
          t("general") ||
          (isReplace ? "Failed to change exercise" : "Failed to add exercise")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const title = useMemo(() => {
    if (isReplace && step === "type") {
      return t("liveTrainingChangeExercisePickType", "Change exercise — choose type");
    }
    if (step === "type") return t("liveTrainingAddExercisePickType");
    if (step === "subtype") return t("liveTrainingAddExercisePickSubtype");
    if (step === "personalized") {
      return t("personalizedSlotTitle", "Personalized training");
    }
    return t("liveTrainingAddExercisePickExercise");
  }, [step, t, isReplace]);

  const breadcrumb = useMemo(() => {
    const parts = [];
    if (selectedType) {
      parts.push(localizedName(selectedType, "type_name", lng));
    }
    if (selectedSubtype) {
      parts.push(localizedName(selectedSubtype, "sub_type_name", lng));
    }
    return parts.filter(Boolean).join(" → ");
  }, [selectedType, selectedSubtype, lng]);

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      className="lt-add-ex-dialog"
      container={portalRoot || undefined}
      disableScrollLock
      sx={{ zIndex: LIVE_TRAINING_DIALOG_Z_INDEX }}
      PaperProps={{
        className: "lt-add-ex-dialog__paper",
      }}
    >
      <div className="lt-add-ex-dialog__header">
        <div className="lt-add-ex-dialog__header-row">
          {step !== "type" ? (
            <button
              type="button"
              className="lt-add-ex-dialog__icon-btn"
              onClick={handleBack}
              disabled={loading || submitting}
              aria-label={t("back") || "Back"}
            >
              <ArrowBackIcon fontSize="small" />
            </button>
          ) : (
            <span className="lt-add-ex-dialog__icon-btn-spacer" />
          )}
          <h2 className="lt-add-ex-dialog__title">{title}</h2>
          <button
            type="button"
            className="lt-add-ex-dialog__icon-btn"
            onClick={onClose}
            disabled={submitting}
            aria-label={t("close") || "Close"}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        {breadcrumb ? (
          <p className="lt-add-ex-dialog__breadcrumb">{breadcrumb}</p>
        ) : null}
      </div>

      <div className="lt-add-ex-dialog__body">
        {loading ? (
          <div className="lt-add-ex-dialog__loader">
            <Loader />
          </div>
        ) : null}

        {error && !loading ? (
          <p className="lt-add-ex-dialog__error" role="alert">
            {error}
          </p>
        ) : null}

        {!loading && step === "type" ? (
          <ul className="lt-add-ex-dialog__list">
            {types.map((row) => (
              <li key={row.type_id}>
                <button
                  type="button"
                  className="lt-add-ex-dialog__item"
                  onClick={() => handleSelectType(row)}
                >
                  {localizedName(row, "type_name", lng)}
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {!loading && step === "subtype" ? (
          <ul className="lt-add-ex-dialog__list">
            {subtypes.map((row) => (
              <li key={row.sub_type_id}>
                <button
                  type="button"
                  className="lt-add-ex-dialog__item"
                  onClick={() => handleSelectSubtype(row)}
                >
                  {localizedName(row, "sub_type_name", lng)}
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {!loading && step === "personalized" ? (
          <div className="lt-add-ex-dialog__slot">
            <p className="lt-add-ex-dialog__slot-lead">
              {t(
                "personalizedSlotLead",
                "Each player runs the exercise Pingflo assigned for their primary SMART objective, after you validate it."
              )}
            </p>
            <label className="lt-add-ex-dialog__duration" htmlFor="lt-personalized-duration">
              {t("personalizedSlotDuration", "Block duration (minutes)")}
              <input
                id="lt-personalized-duration"
                type="number"
                min={1}
                value={durationMin}
                onChange={(e) => setDurationMin(normalizeDuration(e.target.value))}
              />
            </label>
            {slotPlayers.length === 0 ? (
              <p>
                {t(
                  "personalizedSlotEmptyRoster",
                  "No players in this group yet. The block will use each player's validated exercise once they are in the session."
                )}
              </p>
            ) : (
              <ul className="lt-add-ex-dialog__slot-list">
                {slotPlayers.map((player) => {
                  const ex = player.personalized_exercise;
                  const status = String(ex?.status || "").toLowerCase();
                  let line = t(
                    "personalizedSlotMissing",
                    "No validated exercise for the primary objective yet"
                  );
                  if (status === "assigned" && ex?.title) {
                    line = t("personalizedSlotWillRun", {
                      title: ex.title,
                      defaultValue: "Will run: {{title}}",
                    });
                  } else if (ex?.title) {
                    line = t("personalizedSlotAwaitingValidation", {
                      title: ex.title,
                      defaultValue: "Awaiting your validation: {{title}}",
                    });
                  }
                  return (
                    <li key={player.user_id || player.player_id}>
                      <strong>{player.name}</strong>
                      {player.primary_focus ? <span> · {player.primary_focus}</span> : null}
                      <div>{line}</div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ) : null}

        {!loading && step === "exercise" ? (
          <>
            <ul className="lt-add-ex-dialog__list lt-add-ex-dialog__list--exercises">
              {exercises.map((row) => {
                const active = String(row.ex_id) === String(selectedExId);
                const name =
                  localizedName(row, "ex_name", lng) ||
                  row.ex_name ||
                  row.ex_id;
                const pref = getExercisePref(row.ex_id);
                // prefTick forces re-read after star/rating taps
                void prefTick;
                return (
                  <li key={row.ex_id}>
                    <div
                      className={`lt-add-ex-dialog__item-row${
                        active ? " lt-add-ex-dialog__item-row--active" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className={`lt-add-ex-dialog__item${
                          active ? " lt-add-ex-dialog__item--active" : ""
                        }`}
                        onClick={() => {
                          setSelectedExId(row.ex_id);
                          setDurationMin(normalizeDuration(row.duration));
                        }}
                      >
                        <span className="lt-add-ex-dialog__item-name">{name}</span>
                        <span className="lt-add-ex-dialog__item-meta">
                          {normalizeDuration(row.duration)} {t("min") || "min"}
                        </span>
                      </button>
                      <div className="lt-add-ex-dialog__prefs">
                        <button
                          type="button"
                          className={`lt-add-ex-dialog__fav${
                            pref.favorite ? " is-on" : ""
                          }`}
                          aria-pressed={pref.favorite}
                          aria-label={
                            pref.favorite
                              ? t("liveTrainingUnfavoriteExercise", "Remove favorite")
                              : t("liveTrainingFavoriteExercise", "Favorite")
                          }
                          title={t("liveTrainingFavoriteExercise", "Favorite")}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExerciseFavorite(row.ex_id);
                            setExercises((prev) => sortExercisesByCoachPref(prev));
                            setPrefTick((n) => n + 1);
                          }}
                        >
                          {pref.favorite ? (
                            <StarIcon fontSize="inherit" />
                          ) : (
                            <StarBorderIcon fontSize="inherit" />
                          )}
                        </button>
                        <div
                          className="lt-add-ex-dialog__rating"
                          role="group"
                          aria-label={t("liveTrainingRateExercise", "Rate exercise")}
                        >
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button
                              key={n}
                              type="button"
                              className={`lt-add-ex-dialog__rate${
                                pref.rating >= n ? " is-on" : ""
                              }`}
                              aria-label={`${n}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const next =
                                  pref.rating === n ? 0 : n;
                                setExerciseRating(row.ex_id, next);
                                setExercises((prev) =>
                                  sortExercisesByCoachPref(prev)
                                );
                                setPrefTick((v) => v + 1);
                              }}
                            >
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            {selectedExId ? (
              <div className="lt-add-ex-dialog__duration">
                <label htmlFor="lt-add-ex-duration">
                  {t("duration") || "Duration"} ({t("min") || "min"})
                </label>
                <input
                  id="lt-add-ex-duration"
                  type="number"
                  min={1}
                  max={180}
                  value={durationMin}
                  onChange={(e) =>
                    setDurationMin(normalizeDuration(e.target.value))
                  }
                />
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      {step === "personalized" || step === "exercise" ? (
        <div className="lt-add-ex-dialog__footer">
          <button
            type="button"
            className="lt-add-ex-dialog__add-btn"
            disabled={(step === "exercise" && !selectedExId) || submitting}
            onClick={step === "personalized" ? handleAddPersonalized : handleAdd}
          >
            {submitting
              ? isReplace
                ? t("liveTrainingChangingExercise", "Changing…")
                : t("liveTrainingAddingExercise")
              : isReplace
                ? t("liveTrainingConfirmChangeExercise", "Change exercise")
                : t("liveTrainingConfirmAddExercise")}
          </button>
        </div>
      ) : null}
    </Dialog>
  );
}

export default LiveTrainingAddExerciseDialog;
